import unescape from 'lodash/unescape';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {withRouter} from 'react-router-dom';
import withConfig from 'config/withConfig';
import ClipboardJS from 'clipboard';
import IconCenter from '@spothero/icons/center';
import IconPlus from '@spothero/icons/plus';
import IconMinus from '@spothero/icons/minus';
import IconChevronDoubleRight from '@spothero/icons/chevron-double-right';
import IconLocationArrow from '@spothero/icons/location-arrow';
import IconMarker from '@spothero/icons/marker';
import IconShare from '@spothero/icons/share';
import Button from '@spothero/ui/button';
import TextInput from '@spothero/ui/text-input';
import Tooltip from '@spothero/ui/tooltip';
import SearchUtils from '../search-utils';
import {toggleListView} from '../search-actions';

class SpotMapControls extends Component {
    static propTypes = {
        isMobile: PropTypes.bool.isRequired,
        location: PropTypes.shape({
            pathname: PropTypes.string,
        }).isRequired,
        isDestinationOutOfBounds: PropTypes.bool.isRequired,
        isListViewActive: PropTypes.bool.isRequired,
        zoom: PropTypes.number.isRequired,
        destinationTitle: PropTypes.string.isRequired,
        onZoomIn: PropTypes.func.isRequired,
        onZoomOut: PropTypes.func.isRequired,
        onListViewToggle: PropTypes.func.isRequired,
        onGoToCurrentLocation: PropTypes.func,
        onReturnToDestination: PropTypes.func.isRequired,
        starts: PropTypes.string,
        ends: PropTypes.string,
        isMonthly: PropTypes.bool,
        searchString: PropTypes.string,
        latitude: PropTypes.number.isRequired,
        longitude: PropTypes.number.isRequired,
        isPolishTimepickersEnabled: PropTypes.bool,
    };
    static defaultProps = {
        isListViewActive: true,
    };
    state = {
        showShareUrl: false,
        showShareUrlTooltip: false,
    };

    componentDidMount() {
        this._clipboard = new ClipboardJS('.SpotMapControl-share-url-toggle');
    }

    componentDidUpdate(prevProps, prevState) {
        const {showShareUrl} = this.state;
        const {showShareUrl: prevShowShareUrl} = prevState;

        if (showShareUrl && showShareUrl !== prevShowShareUrl) {
            this._shareUrl.node.select();
        }
    }

    componentWillUnmount() {
        this._clipboard.destroy();

        if (this._shareUrlTooltipShowTimeout) {
            clearTimeout(this._shareUrlTooltipShowTimeout);
            clearTimeout(this._shareUrlTooltipHideTimeout);
        }
    }

    _onShareUrlToggleClick = evt => {
        const {showShareUrl} = this.state;

        this.setState((prevState, props) => {
            return {
                showShareUrl: !prevState.showShareUrl,
            };
        });

        // if wasn't already open, then run tooltip timeouts
        if (!showShareUrl) {
            this._shareUrlTooltipShowTimeout = setTimeout(() => {
                this.setState((prevState, props) => {
                    return {
                        showShareUrlTooltip: !prevState.showShareUrlTooltip,
                    };
                });
            }, 250);

            this._shareUrlTooltipHideTimeout = setTimeout(() => {
                if (this._shareUrlTooltip) {
                    this._shareUrlTooltip.hide();
                }
            }, 2000);
        }
    };

    _onShareUrlClick = () => {
        this._shareUrl.node.select();
    };

    _onTooltipHidden = () => {
        this.setState({
            showShareUrlTooltip: false,
        });
    };

    render() {
        const {
            isMobile,
            location: {pathname},
            isDestinationOutOfBounds,
            isListViewActive,
            zoom,
            destinationTitle,
            onZoomIn,
            onZoomOut,
            onListViewToggle,
            onGoToCurrentLocation,
            onReturnToDestination,
            starts,
            ends,
            isMonthly,
            searchString,
            latitude,
            longitude,
            isPolishTimepickersEnabled,
        } = this.props;
        const {showShareUrl, showShareUrlTooltip} = this.state;
        const shareUrlToggleClasses = classNames(
            'SpotMapControl',
            'SpotMapControl-share-url-toggle',
            {'SpotMapControl-active': showShareUrl}
        );
        const shareUrl = SearchUtils.getShareURL({
            pathname,
            starts,
            ends,
            isMonthly,
            searchString,
            latitude,
            longitude,
        });
        const additionalInputProps = {
            onClick: this._onShareUrlClick,
        };

        return (
            <div className="SpotMapControls">
                {!isMobile && (
                    <div className="SpotMapControl SpotMapControl-zoom">
                        <Button
                            className="SpotMapControl-zoom-in"
                            onClick={onZoomIn}
                            disabled={zoom > 19}
                        >
                            <IconPlus />
                        </Button>
                        <Button
                            className="SpotMapControl-zoom-out"
                            onClick={onZoomOut}
                            disabled={zoom < 4}
                        >
                            <IconMinus />
                        </Button>
                    </div>
                )}
                {!isMobile && !isListViewActive && (
                    <Button
                        className="SpotMapControl SpotMapControl-list-view-toggle"
                        onClick={onListViewToggle}
                    >
                        <IconChevronDoubleRight />
                    </Button>
                )}
                {isMobile && onGoToCurrentLocation && (
                    <Button
                        className="SpotMapControl SpotMapControl-go-to-current-location"
                        onClick={onGoToCurrentLocation}
                    >
                        <IconLocationArrow />
                    </Button>
                )}
                {!isMobile && isDestinationOutOfBounds && (
                    <Button
                        className="SpotMapControl SpotMapControl-return-to-destination"
                        iconPosition="left"
                        color="secondary"
                        width="narrow"
                        onClick={onReturnToDestination}
                    >
                        {isPolishTimepickersEnabled ? (
                            <IconCenter />
                        ) : (
                            <>
                                <IconMarker /> Return to{' '}
                                {unescape(destinationTitle)}
                            </>
                        )}
                    </Button>
                )}
                {!isPolishTimepickersEnabled && (
                    <Button
                        className={shareUrlToggleClasses}
                        onClick={this._onShareUrlToggleClick}
                        data-clipboard-text={shareUrl}
                    >
                        <IconShare />
                    </Button>
                )}
                {showShareUrl && (
                    <span>
                        <TextInput
                            ref={node => {
                                this._shareUrl = node;
                            }}
                            className="SpotMapControl-share-url"
                            defaultValue={shareUrl}
                            additionalInputProps={additionalInputProps}
                            autoFocus
                            select
                        />
                        {showShareUrlTooltip && (
                            <Tooltip
                                ref={node => {
                                    this._shareUrlTooltip = node;
                                }}
                                showClose={false}
                                position="top"
                                triggerSelector=".SpotMapControl-share-url"
                                onHidden={this._onTooltipHidden}
                            >
                                Copied to Clipboard
                            </Tooltip>
                        )}
                    </span>
                )}
            </div>
        );
    }
}

const mapStateToProps = state => {
    const {
        search: {
            data: {isListVisible},
        },
        searchRequest: {
            starts,
            ends,
            monthly,
            latitude,
            longitude,
            search_string: searchString,
        },
    } = state;

    return {
        isListViewActive: isListVisible,
        starts,
        ends,
        isMonthly: monthly,
        searchString,
        latitude,
        longitude,
    };
};

const mapDispatchToProps = {
    onListViewToggle: toggleListView,
};

const enhance = compose(
    withConfig(['isMobile']),
    withRouter,
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
);

export default enhance(SpotMapControls);
